// File: src/components/LiveRCImport.js
// Importing a race result from LiveRC.
//
// The operator pastes one event URL; everything else is read off the page.
// LiveRC publishes an event's classes together on two pages - "Final Results"
// and "Results (best N of M)" - so one fetch of each covers whichever class this
// race happens to be, and the class is picked here rather than in the URL.
//
// Deliberately narrower than MyRCMImport: there is no event browser and no
// report key, and the matching and points rules are shared rather than copied.

import React, { useState } from 'react';
import { X, FileSpreadsheet, User, Trophy } from 'lucide-react';
import { API_BASE_URL } from '../App';
import { calculatePointsForPosition, calculateQualificationPoints } from '../utils/utils';
import { fuzzyMatchDriver, matchNeedsReview } from '../utils/driverMatching';
import {
  parseLiveRCUrl, buildLiveRCEventUrl, parseLiveRCEventPage, parseLiveRCRankingTables
} from '../utils/liverc';
import { useLanguage } from '../contexts/LanguageContext';
import { handleUnauthorized } from '../utils/session';

// Only the top five score qualification points, the same rule the MyRCM import
// and the manual entry screen follow.
const QUALIFYING_PLACES_SCORED = 5;

function LiveRCImport({ raceId, seriesClass, token, onClose, loadSeries }) {
  const { t } = useLanguage();

  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info');
  // What the event page said it was, so the operator can see they picked the
  // right night before importing anything.
  const [event, setEvent] = useState(null);
  // Every class LiveRC listed, and the one being imported.
  const [classes, setClasses] = useState([]);
  const [qualifyingClasses, setQualifyingClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [preview, setPreview] = useState(null);
  const [showDriverMapping, setShowDriverMapping] = useState(false);

  const wantsQualifying = Boolean(seriesClass.calculate_qualification_points);

  const say = (text, type = 'info') => { setMessage(text); setMessageType(type); };

  // Fetch a LiveRC page through the server. The browser cannot: LiveRC sends no
  // CORS headers.
  const fetchPage = async (target) => {
    const response = await fetch(
      `${API_BASE_URL}/proxy/liverc?url=${encodeURIComponent(target)}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    if (handleUnauthorized(response)) return null;
    // LiveRC answers 404 for an event id it does not have, and the proxy passes
    // that through. "No such event" and "LiveRC is unreachable" are different
    // problems, and only one of them is the operator's to fix.
    if (response.status === 404) throw new Error(t('livercEventNotFound'));
    if (!response.ok) throw new Error(t('livercFetchFailed'));

    return new DOMParser().parseFromString(await response.text(), 'text/html');
  };

  const resetResults = () => {
    setEvent(null);
    setClasses([]);
    setQualifyingClasses([]);
    setSelectedClass('');
    setPreview(null);
    setShowDriverMapping(false);
  };

  const fetchEvent = async () => {
    const parsed = parseLiveRCUrl(url);
    if (parsed.error) return say(t(parsed.error), 'error');

    setLoading(true);
    say('');
    resetResults();

    try {
      const eventDoc = await fetchPage(buildLiveRCEventUrl(parsed));
      if (!eventDoc) return;

      const details = parseLiveRCEventPage(eventDoc, parsed.origin);
      if (!details.finalResultsUrl) throw new Error(t('livercEventNotFound'));

      const finalDoc = await fetchPage(details.finalResultsUrl);
      if (!finalDoc) return;

      const finalClasses = parseLiveRCRankingTables(finalDoc);
      if (!finalClasses || finalClasses.length === 0) throw new Error(t('livercNoResultsTable'));

      // The qualifying order is a separate page, and an event with no qualifying
      // rounds simply does not have one. That is normal for a club night, so it
      // is not an error - the qualification columns just stay empty.
      let qualifying = [];
      if (wantsQualifying && details.pointsUrl) {
        const pointsDoc = await fetchPage(details.pointsUrl);
        if (pointsDoc) qualifying = parseLiveRCRankingTables(pointsDoc) || [];
      }

      setEvent(details);
      setClasses(finalClasses);
      setQualifyingClasses(qualifying);
      // One class needs no choosing.
      setSelectedClass(finalClasses.length === 1 ? finalClasses[0].className : '');
      say(t('livercEventLoaded').replace('{count}', String(finalClasses.length)), 'success');
    } catch (error) {
      say(`${t('error')}: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Build the preview for one class: match every driver, work out the points.
  const buildPreview = (className) => {
    const chosen = classes.find(c => c.className === className);
    if (!chosen) return;

    // LiveRC gives no car numbers on these pages, so matching is by name alone.
    const results = chosen.rows.map(({ rank, driverName }) => {
      const match = fuzzyMatchDriver(driverName, null, seriesClass.drivers || []);

      return {
        driver_id: match ? match.driver.id : null,
        driver_name: match ? match.driver.name : null,
        car_number: null,
        imported_name: driverName,
        position: rank,
        points: calculatePointsForPosition(rank),
        qualification_position: null,
        qualification_points: 0,
        isNew: !match,
        isMatched: Boolean(match),
        needsReview: matchNeedsReview(match),
        manualMatch: null
      };
    });

    // Qualifying is matched to the final results by name: it is the same event,
    // so the spelling agrees even when our own driver table does not.
    const qualifying = qualifyingClasses.find(c => c.className === className);
    if (qualifying) {
      for (const { rank, driverName } of qualifying.rows) {
        if (rank > QUALIFYING_PLACES_SCORED) continue;
        const result = results.find(
          r => r.imported_name.toLowerCase() === driverName.toLowerCase());
        if (result) {
          result.qualification_position = rank;
          result.qualification_points = calculateQualificationPoints(rank);
        }
      }
    }

    setPreview({
      className,
      results,
      newDrivers: results.filter(r => r.isNew).map(r => r.imported_name),
      hasUnmatched: results.some(r => r.isNew),
      hasQualifying: Boolean(qualifying)
    });
    setShowDriverMapping(false);
  };

  const chooseClass = (className) => {
    setSelectedClass(className);
    setPreview(null);
    if (className) buildPreview(className);
  };

  const updateDriverMapping = (importedName, selectedDriverId) => {
    setPreview(prev => {
      const results = prev.results.map(result => {
        if (result.imported_name !== importedName) return result;
        if (selectedDriverId === 'new') {
          return { ...result, isNew: true, isMatched: false, manualMatch: null, driver_id: null };
        }
        const driver = seriesClass.drivers.find(d => d.id === parseInt(selectedDriverId, 10));
        if (!driver) return result;
        return {
          ...result,
          driver_id: driver.id,
          driver_name: driver.name,
          isNew: false,
          isMatched: true,
          needsReview: false,
          manualMatch: driver.name
        };
      });

      return {
        ...prev,
        results,
        newDrivers: results.filter(r => r.isNew).map(r => r.imported_name),
        hasUnmatched: results.some(r => r.isNew)
      };
    });
  };

  const importResults = async () => {
    if (!preview || preview.results.length === 0) return;

    setLoading(true);

    try {
      // Create the drivers this class has never seen, then write the rankings.
      const createdIds = {};
      const toCreate = preview.results.filter(r => r.isNew && !r.driver_id);

      for (const result of toCreate) {
        const response = await fetch(`${API_BASE_URL}/drivers`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            name: result.imported_name,
            car_number: null,
            series_class_id: seriesClass.id
          })
        });

        if (handleUnauthorized(response)) return;
        if (!response.ok) {
          const failed = await response.json().catch(() => ({}));
          throw new Error(failed.error || t('failedToCreateDriver'));
        }

        createdIds[result.imported_name] = (await response.json()).id;
      }

      const rankings = preview.results.map(result => ({
        driverId: result.driver_id || createdIds[result.imported_name],
        position: result.position,
        qualificationPosition: result.qualification_position,
        points: result.points,
        qualificationPoints: result.qualification_points
      }));

      const response = await fetch(`${API_BASE_URL}/races/${raceId}/rankings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ rankings })
      });

      if (handleUnauthorized(response)) return;
      if (!response.ok) {
        const failed = await response.json().catch(() => ({}));
        throw new Error(failed.error || String(response.status));
      }

      say(
        t('livercImported')
          .replace('{count}', String(preview.results.length))
          .replace('{class}', preview.className) +
        (toCreate.length > 0
          ? `\n\n${toCreate.length} ${t('newDriversWillBeCreated')}\n${toCreate.map(d => d.imported_name).join(', ')}`
          : ''),
        'success'
      );

      await loadSeries();
      setTimeout(onClose, 3000);
    } catch (error) {
      say(`${t('error')}: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-paper rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-line">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-2xl font-bold text-ink">{t('importFromLiveRC')}</h3>
          <button onClick={onClose} className="text-ink-muted hover:text-ink" aria-label={t('close')}>
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="space-y-4">
          <div className="bg-blue-50 dark:bg-blue-500/20 border border-blue-500 text-blue-800 dark:text-blue-200 px-4 py-3 rounded">
            <p className="font-semibold mb-2">{t('howToImportFromLiveRC')}</p>
            <ol className="list-decimal list-inside space-y-1 text-sm">
              <li>{t('livercStep1')}</li>
              <li>{t('livercStep2')}</li>
              <li>{t('livercStep3')}</li>
            </ol>
          </div>

          <div>
            <label className="block text-ink-muted mb-2 font-semibold" htmlFor="liverc-url">
              {t('livercUrlLabel')} <span className="text-red-700 dark:text-red-400">*</span>
            </label>
            <input
              id="liverc-url"
              type="text"
              value={url}
              onChange={(e) => { setUrl(e.target.value); resetResults(); }}
              placeholder="https://gandsraceway.liverc.com/results/?p=view_event&id=515151"
              className="w-full px-4 py-2 bg-paper-alt border border-line rounded text-ink"
              disabled={loading}
            />
            <p className="text-sm text-ink-muted mt-1">{t('livercUrlHint')}</p>
          </div>

          <button
            onClick={fetchEvent}
            disabled={loading || !url}
            className="w-full bg-accent hover:opacity-90 disabled:bg-paper-alt disabled:text-ink-muted text-accent-ink py-3 rounded font-semibold flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                {t('livercFetching')}
              </>
            ) : (
              <>
                <FileSpreadsheet className="w-5 h-5" />
                {t('fetchAndPreview')}
              </>
            )}
          </button>

          {message && (
            <div className={`px-4 py-3 rounded ${
              messageType === 'success'
                ? 'bg-green-50 dark:bg-green-500/20 border border-green-500 text-green-800 dark:text-green-200'
                : messageType === 'error'
                ? 'bg-red-50 dark:bg-red-500/20 border border-red-500 text-red-800 dark:text-red-200'
                : 'bg-blue-50 dark:bg-blue-500/20 border border-blue-500 text-blue-800 dark:text-blue-200'
            }`}>
              <pre className="whitespace-pre-wrap font-sans">{message}</pre>
            </div>
          )}

          {/* Which event this is, and which of its classes this race is. */}
          {event && classes.length > 0 && (
            <div className="bg-paper-alt border border-line rounded p-4">
              <div className="flex items-center gap-2 text-ink mb-3">
                <Trophy className="w-4 h-4 text-accent" />
                <span className="font-semibold">{event.eventName}</span>
                <span className="text-ink-muted text-sm">
                  {event.trackName}{event.eventDate ? ` · ${event.eventDate}` : ''}
                </span>
              </div>

              <label className="block text-ink-muted mb-2 font-semibold" htmlFor="liverc-class">
                {t('livercPickClass')}
              </label>
              <select
                id="liverc-class"
                value={selectedClass}
                onChange={(e) => chooseClass(e.target.value)}
                className="w-full px-4 py-2 bg-paper border border-line rounded text-ink"
                disabled={loading}
              >
                <option value="">{t('livercPickClassPlaceholder')}</option>
                {classes.map(entry => (
                  <option key={entry.className} value={entry.className}>
                    {entry.className} ({entry.rows.length})
                  </option>
                ))}
              </select>

              {wantsQualifying && !event.pointsUrl && (
                <p className="text-sm text-ink-muted mt-2">{t('livercNoQualifying')}</p>
              )}
            </div>
          )}

          {preview && (
            <div className="border border-line rounded p-4">
              <div className="flex justify-between items-center mb-3">
                <h4 className="text-lg font-bold text-ink">
                  {t('preview')}
                  <span className="block text-sm font-normal text-ink-muted">
                    {event.eventName} — {preview.className}
                  </span>
                </h4>
                {preview.hasUnmatched && (
                  <button
                    onClick={() => setShowDriverMapping(!showDriverMapping)}
                    className="bg-yellow-600 hover:bg-yellow-700 text-white px-3 py-1 rounded text-sm flex items-center gap-2"
                  >
                    <User className="w-4 h-4" />
                    {showDriverMapping ? t('hideDriverMapping') : t('showDriverMapping')}
                  </button>
                )}
              </div>

              <div className="bg-blue-50 dark:bg-blue-500/20 border border-blue-500 text-blue-800 dark:text-blue-200 px-4 py-2 rounded mb-4">
                <strong>✓ {preview.results.length} {t('resultsReadyToImport')}</strong>
                {preview.newDrivers.length > 0 && (
                  <div className="mt-2 text-sm">
                    <span className="bg-orange-600 text-white px-2 py-1 rounded text-xs">{t('unmatched')}</span>
                    {' '}{preview.newDrivers.length} {t('newDriversWillBeCreated')}
                  </div>
                )}
              </div>

              {showDriverMapping && preview.hasUnmatched && (
                <div className="bg-yellow-50 dark:bg-yellow-500/20 border border-yellow-500 rounded p-4 mb-4">
                  <h5 className="text-ink font-bold mb-3">{t('matchDrivers')}</h5>
                  <p className="text-yellow-800 dark:text-yellow-200 text-sm mb-3">
                    {t('someDriversNotMatched')}
                  </p>
                  {preview.results.filter(r => !r.isMatched).map(result => (
                    <div key={result.imported_name} className="bg-paper-alt rounded p-3 mb-2">
                      <div className="flex items-center gap-3">
                        <span className="text-ink font-medium flex-1">{result.imported_name}</span>
                        <select
                          value={result.driver_id || 'new'}
                          onChange={(e) => updateDriverMapping(result.imported_name, e.target.value)}
                          className="px-3 py-1 bg-paper-alt border border-line rounded text-ink text-sm"
                          aria-label={result.imported_name}
                        >
                          <option value="new">{t('livercCreateNewDriver')}</option>
                          <optgroup label={t('matchToExistingDriver')}>
                            {seriesClass.drivers?.map(driver => (
                              <option key={driver.id} value={driver.id}>
                                {driver.car_number ? `#${driver.car_number} - ` : ''}{driver.name}
                                {driver.club ? ` (${driver.club})` : ''}
                              </option>
                            ))}
                          </optgroup>
                        </select>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-line">
                      <th className="text-left text-ink-muted pb-2 pr-4">{t('driver')}</th>
                      <th className="text-center text-ink-muted pb-2 px-2">{t('pos')}</th>
                      <th className="text-center text-ink-muted pb-2 px-2">{t('pts')}</th>
                      {wantsQualifying && (
                        <>
                          <th className="text-center text-ink-muted pb-2 px-2">{t('qPos')}</th>
                          <th className="text-center text-ink-muted pb-2 px-2">{t('qPts')}</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.results.map(result => (
                      <tr key={result.imported_name} className="border-b border-line">
                        <td className="py-2 pr-4">
                          <div>
                            <span className="text-ink">{result.driver_name || result.imported_name}</span>
                            {result.isNew && (
                              <span className="ml-2 bg-orange-600 text-white px-2 py-0.5 rounded text-xs">{t('new')}</span>
                            )}
                            {result.manualMatch && (
                              <span className="ml-2 bg-green-600 text-white px-2 py-0.5 rounded text-xs">{t('matched')}</span>
                            )}
                          </div>
                          {result.driver_name && result.imported_name !== result.driver_name && (
                            <div className="text-xs text-ink-muted">{t('livercLabel')} {result.imported_name}</div>
                          )}
                        </td>
                        <td className="text-center py-2 px-2">
                          <span className="text-ink font-semibold">P{result.position}</span>
                        </td>
                        <td className="text-center py-2 px-2">
                          <span className="text-yellow-700 dark:text-yellow-400 font-mono-num">{result.points}</span>
                        </td>
                        {wantsQualifying && (
                          <>
                            <td className="text-center py-2 px-2">
                              {result.qualification_position ? (
                                <span className="text-green-700 dark:text-green-400">Q{result.qualification_position}</span>
                              ) : (
                                <span className="text-ink-muted">-</span>
                              )}
                            </td>
                            <td className="text-center py-2 px-2">
                              {result.qualification_points > 0 ? (
                                <span className="text-green-700 dark:text-green-400 font-mono-num">{result.qualification_points}</span>
                              ) : (
                                <span className="text-ink-muted">-</span>
                              )}
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <button
                onClick={importResults}
                disabled={loading}
                className="mt-4 w-full bg-green-600 hover:bg-green-700 disabled:bg-paper-alt disabled:text-ink-muted text-white py-3 rounded font-semibold"
              >
                {loading ? t('importing') : t('importResults').replace('{count}', preview.results.length)}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default LiveRCImport;
